import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import ForcePasswordChange from './pages/ForcePasswordChange';
import Payments from './pages/Payments';
import Users from './pages/Users';
import References from './pages/References';
import Project from './pages/Project';
import Contractors from './pages/Contractors';
import FinanceLayout from './pages/finance/FinanceLayout';
import Dashboard from './pages/finance/Dashboard';
import CeoDashboard from './pages/finance/CeoDashboard';
import CeoModule from './pages/finance/CeoModule';
import Cashflow from './pages/finance/Cashflow';
import Queue from './pages/finance/Queue';
import Rules from './pages/finance/Rules';
import DealDetail from './pages/finance/DealDetail';
import PnL from './pages/finance/PnL';
import Bank from './pages/finance/Bank';
import Stub from './pages/finance/Stub';
import Designers from './pages/Designers';
import Projects from './pages/Projects';

function PrivateRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="spinner">Загрузка...</div>;
  // Сохраняем адрес, куда шёл пользователь (например, прямая ссылка на заявку из письма),
  // чтобы после входа вернуть его туда же, а не на дефолтную страницу.
  if (!user) return <Navigate to="/login" state={{ from: location }} />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
}

// Дизайнер не имеет доступа к разделу «Заявки» (там же обычно /payments по умолчанию) —
// у него свой раздел. Админу при входе сразу открывается дашборд с ключевыми показателями
// бизнеса, а не заявки.
function IndexRedirect() {
  const { user } = useAuth();
  const target = user.can_view_ceo_dashboard ? '/finance/dashboard'
    : user.role === 'designer' ? '/designers' : user.role === 'admin' ? '/finance' : '/payments';
  return <Navigate to={target} />;
}

export default function App() {
  const { user, loading } = useAuth();
  if (loading) return <div className="spinner">Загрузка...</div>;

  // Гейт на уровне всего приложения: пока флаг не снят, пользователь не может
  // попасть ни на один другой роут (в т.ч. прямым переходом по URL) — раньше
  // это проверялось только внутри Login.js, и роут "/login" редиректил на "/"
  // сразу как только user становился truthy, минуя экран смены пароля.
  if (user?.must_change_password) return <ForcePasswordChange />;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<IndexRedirect />} />
        <Route path="payments" element={<PrivateRoute roles={['admin','accountant','manager','rop']}><Payments /></PrivateRoute>} />
        <Route path="payments/:id" element={<PrivateRoute roles={['admin','accountant','manager','rop']}><Payments /></PrivateRoute>} />
        <Route path="designers" element={<PrivateRoute roles={['admin','manager','rop','designer']}><Designers /></PrivateRoute>} />
        <Route path="users" element={<PrivateRoute roles={['admin']}><Users /></PrivateRoute>} />
        <Route path="references" element={<PrivateRoute roles={['admin']}><References /></PrivateRoute>} />
        <Route path="contractors" element={<PrivateRoute roles={['admin','accountant','manager','rop']}><Contractors /></PrivateRoute>} />
        <Route path="projects" element={<PrivateRoute roles={['admin','accountant','manager','rop','designer']}><Projects /></PrivateRoute>} />
        <Route path="projects/deal/:id" element={<PrivateRoute roles={['admin','accountant','manager','rop']}><DealDetail /></PrivateRoute>} />
        <Route path="projects/:name" element={<PrivateRoute><Project /></PrivateRoute>} />
        <Route path="finance" element={<PrivateRoute roles={['admin','accountant','rop']}><FinanceLayout /></PrivateRoute>}>
          <Route index element={<PrivateRoute roles={['admin','accountant','rop']}><Dashboard /></PrivateRoute>} />
          <Route path="dashboard" element={user?.can_view_ceo_dashboard ? <CeoDashboard /> : <Navigate to="/finance" replace />} />
          <Route path="dashboard/:module" element={user?.can_view_ceo_dashboard ? <CeoModule /> : <Navigate to="/finance" replace />} />
          <Route path="categorization" element={<Navigate to="/finance/queue" replace />} />
          <Route path="cashflow" element={<PrivateRoute roles={['admin','accountant']}><Cashflow /></PrivateRoute>} />
          <Route path="queue" element={<PrivateRoute roles={['admin','accountant']}><Queue /></PrivateRoute>} />
          <Route path="rules" element={<PrivateRoute roles={['admin','accountant']}><Rules /></PrivateRoute>} />
          <Route path="pnl" element={<PrivateRoute roles={['admin','accountant','rop']}><PnL /></PrivateRoute>} />
          <Route path="bank" element={<PrivateRoute roles={['admin','accountant']}><Bank /></PrivateRoute>} />
          <Route path="balance" element={<PrivateRoute roles={['admin','accountant']}><Stub title="Баланс" /></PrivateRoute>} />
          <Route path="odds" element={<PrivateRoute roles={['admin','accountant']}><Stub title="ОДДС" /></PrivateRoute>} />
          <Route path="calendar" element={<PrivateRoute roles={['admin','accountant']}><Stub title="Платёжный календарь" /></PrivateRoute>} />
        </Route>
      </Route>
    </Routes>
  );
}
